import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Siren, ShieldCheck, FileSignature, ClipboardList, Loader2, X, CheckCircle2, Smartphone, ArrowLeft } from 'lucide-react';
import { Button } from '../components';
import {
  bereidCrisisWizardVoor,
  vraagCrisisAddendumCode,
  ondertekenCrisisAddendum,
  euroCrisis,
  type CrisisSector,
  type CrisisSectorCode,
  type CrisisTier,
  type CrisisWizardVoorbereiding,
  type CrisisEmployerOverzicht,
} from '../services/crisisApi';

type Stap = 1 | 2 | 3;

const STAPPEN: Array<{ nr: Stap; titel: string; sub: string; icon: React.ComponentType<{ className?: string }> }> = [
  { nr: 1, titel: 'Wettelijke screening', sub: 'De invoer', icon: ClipboardList },
  { nr: 2, titel: 'Tarief & dekking', sub: 'De output', icon: ShieldCheck },
  { nr: 3, titel: 'Digitale SMS-verzegeling', sub: 'De handtekening', icon: FileSignature },
];

function tierVoor(tiers: CrisisTier[], aantal: number): CrisisTier | null {
  return tiers.find((t) => aantal >= t.min_medewerkers && aantal <= t.max_medewerkers) ?? null;
}

interface Props {
  locaties: CrisisEmployerOverzicht['locaties'];
  tiers: CrisisTier[];
  sectoren: CrisisSector[];
  /** Vestiging die vooraf geselecteerd staat (bijv. vanuit de knop per vestiging). */
  startLocatieId?: string | null;
  onClose: () => void;
}

/**
 * 🚨 Calamiteiten-onboarding wizard — 3 stappen, vederlicht, in een modal boven de Employer Cockpit.
 *  1. Wettelijke screening: sector, ploegendienst, interne contactpersoon (Hoofd BHV / HR) + mobiel.
 *  2. Tarief- & dekkingsbevestiging: schil realtime uit de staffel → contractuele samenvatting.
 *  3. Addendum Crisisdienst in een scrolbaar tekstvak → akkoord → 6-cijferige SMS-code → ACTIVE.
 */
export const EmployerCrisisWizard: React.FC<Props> = ({ locaties, tiers, sectoren, startLocatieId, onClose }) => {
  const queryClient = useQueryClient();
  const [stap, setStap] = useState<Stap>(1);

  // Stap 1
  const [locatieId, setLocatieId] = useState<string>(startLocatieId ?? locaties[0]?.id ?? '');
  const [aantal, setAantal] = useState<string>('');
  const [sector, setSector] = useState<CrisisSectorCode | ''>('');
  const [ploegendienst, setPloegendienst] = useState<boolean | null>(null);
  const [contactNaam, setContactNaam] = useState('');
  const [contactTelefoon, setContactTelefoon] = useState('');

  // Stap 2/3
  const [voorbereiding, setVoorbereiding] = useState<CrisisWizardVoorbereiding | null>(null);
  const [akkoord, setAkkoord] = useState(false);
  const [codeVerstuurd, setCodeVerstuurd] = useState<string | null>(null);
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [gelukt, setGelukt] = useState<'ACTIVE' | 'PENDING_MANDATE' | null>(null);
  const codeRefs = useRef<Array<HTMLInputElement | null>>([]);

  const locatie = locaties.find((l) => l.id === locatieId) ?? null;
  const aantalNum = parseInt(aantal, 10);
  const tier = Number.isFinite(aantalNum) ? tierVoor(tiers, aantalNum) : null;

  const stap1Compleet = Boolean(locatie && locatie.regio_code && tier && sector && ploegendienst !== null && contactNaam.trim().length >= 2 && /^(?:\+31|0031|0)6\d{8}$/.test(contactTelefoon.replace(/[\s\-()]/g, '')));

  const voorbereidMutation = useMutation({
    mutationFn: () =>
      bereidCrisisWizardVoor({
        location_id: locatieId,
        aantal_medewerkers: aantalNum,
        sector: sector as CrisisSectorCode,
        ploegendienst: ploegendienst === true,
        contact_naam: contactNaam.trim(),
        contact_telefoon: contactTelefoon.trim(),
      }),
    onSuccess: (r) => {
      setVoorbereiding(r);
      setAkkoord(false);
      setCodeVerstuurd(null);
      setCode(['', '', '', '', '', '']);
      setStap(2);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.response?.data?.errors?.[0] ?? 'Screening opslaan mislukt'),
  });

  const codeMutation = useMutation({
    mutationFn: () => vraagCrisisAddendumCode(voorbereiding!.subscriptionId),
    onSuccess: (r) => {
      setCodeVerstuurd(r.telefoon_masker);
      setCode(['', '', '', '', '', '']);
      setTimeout(() => codeRefs.current[0]?.focus(), 50);
    },
    onError: (e: any) => {
      setAkkoord(false);
      toast.error(e?.response?.data?.message ?? 'SMS-code versturen mislukt');
    },
  });

  const ondertekenMutation = useMutation({
    mutationFn: () => ondertekenCrisisAddendum(voorbereiding!.subscriptionId, code.join('')),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['crisis', 'employer-overzicht'] });
      if (r.status === 'ACTIVE') {
        setGelukt('ACTIVE');
        toast.success('Crisisdienst geactiveerd — de rode alarmknop staat live');
      } else if (r.checkoutUrl) {
        setGelukt('PENDING_MANDATE');
        toast.success('Addendum verzegeld — u wordt doorgestuurd naar de SEPA-machtiging');
        window.location.href = r.checkoutUrl;
      }
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? e?.response?.data?.errors?.[0] ?? 'Ondertekenen mislukt');
      setCode(['', '', '', '', '', '']);
      setTimeout(() => codeRefs.current[0]?.focus(), 50);
    },
  });

  // Akkoord-vinkje → direct SMS-code (passwordless handtekening-engine)
  useEffect(() => {
    if (akkoord && voorbereiding && !codeVerstuurd && !codeMutation.isPending) codeMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akkoord]);

  const codeCompleet = code.every((c) => /^\d$/.test(c));
  useEffect(() => {
    if (codeCompleet && codeVerstuurd && !ondertekenMutation.isPending && !gelukt) ondertekenMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeCompleet]);

  const zetCijfer = (i: number, waarde: string) => {
    const cijfers = waarde.replace(/\D/g, '');
    if (!cijfers) {
      setCode((c) => c.map((x, j) => (j === i ? '' : x)));
      return;
    }
    setCode((c) => {
      const n = [...c];
      for (let k = 0; k < cijfers.length && i + k < 6; k++) n[i + k] = cijfers[k];
      return n;
    });
    const volgende = Math.min(i + cijfers.length, 5);
    codeRefs.current[volgende]?.focus();
  };

  const overzicht = voorbereiding?.overzicht;
  const sectorLabel = useMemo(() => sectoren.find((s) => s.code === sector)?.label ?? '', [sectoren, sector]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-xl bg-white shadow-xl">
        {/* Kop + stappenbalk */}
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Siren className="w-5 h-5 text-red-600" /> Activeer 24/7 Landelijke Crisisdienst
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Sluiten">
            <X className="w-5 h-5" />
          </button>
        </div>
        <ol className="grid grid-cols-3 border-b bg-gray-50 text-xs">
          {STAPPEN.map((s) => {
            const Icon = s.icon;
            const actief = s.nr === stap;
            const klaar = s.nr < stap || gelukt;
            return (
              <li key={s.nr} className={`flex items-center gap-2 px-4 py-3 ${actief ? 'text-[#0B1F3A]' : klaar ? 'text-emerald-700' : 'text-gray-400'}`}>
                <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${actief ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white' : klaar ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-gray-300'}`}>
                  {klaar ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5" />}
                </span>
                <span>
                  <span className="font-semibold">Stap {s.nr}: {s.titel}</span>
                  <span className="hidden sm:inline text-gray-500"> · {s.sub}</span>
                </span>
              </li>
            );
          })}
        </ol>

        <div className="p-5 space-y-5">
          {/* ===== STAP 1: Wettelijke screening ===== */}
          {stap === 1 && (
            <>
              <p className="text-sm text-gray-600">
                Zodat ons landelijke crisisteam bij een calamiteit direct met de juiste context uitrukt, beantwoordt u drie korte vinkvragen.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Vestiging</span>
                  <select value={locatieId} onChange={(e) => setLocatieId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    {locaties.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.naam} {l.plaats ? `(${l.plaats})` : ''} {l.is_hoofdvestiging ? '· hoofdvestiging' : ''}
                      </option>
                    ))}
                  </select>
                  {locatie && !locatie.regio_code && <span className="block mt-1 text-xs text-amber-700">⚠️ Postcode ontbreekt — vul deze in bij Organisatieprofiel (bepaalt de crisisregio).</span>}
                </label>
                <label className="text-sm">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Aantal medewerkers op deze vestiging</span>
                  <input type="number" min={1} value={aantal} onChange={(e) => setAantal(e.target.value)} placeholder="bijv. 150" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  <span className="block mt-1 text-xs text-gray-500">
                    {tier ? (
                      <>
                        Staffel: Schil {tier.schil_niveau} · {tier.naam} · <strong className="text-[#0B1F3A]">{euroCrisis(tier.maandelijkse_fee, 0)}/maand</strong>
                      </>
                    ) : aantal ? (
                      <span className="text-amber-700">De crisisdienst is beschikbaar vanaf 50 medewerkers per vestiging.</span>
                    ) : (
                      'De software trekt realtime de juiste schil uit de database.'
                    )}
                  </span>
                </label>
              </div>

              <fieldset>
                <legend className="text-sm font-medium text-gray-900">1. Wat is de primaire sector van deze vestiging?</legend>
                <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-2">
                  {sectoren.map((s) => {
                    const actief = sector === s.code;
                    return (
                      <button key={s.code} type="button" onClick={() => setSector(s.code)} className={`rounded-lg border p-3 text-left text-sm ${actief ? 'border-[#0B1F3A] bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <span className="mr-1">{actief ? '🔘' : '⚪'}</span>
                        <span className="font-medium text-gray-900">{s.label}</span>
                        <span className="block text-[11px] text-gray-500 mt-0.5">{s.omschrijving}</span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <fieldset>
                <legend className="text-sm font-medium text-gray-900">2. Is er op deze locatie een actieve Ploegendienst of Nachtdienst aanwezig?</legend>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { w: true, tekst: 'Ja, we werken 24/7 in ploegen' },
                    { w: false, tekst: 'Nee, uitsluitend reguliere kantooruren' },
                  ].map((o) => {
                    const actief = ploegendienst === o.w;
                    return (
                      <button key={String(o.w)} type="button" onClick={() => setPloegendienst(o.w)} className={`rounded-lg border p-3 text-left text-sm ${actief ? 'border-[#0B1F3A] bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <span className="mr-1">{actief ? '🔘' : '⚪'}</span>
                        {o.tekst}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <fieldset>
                <legend className="text-sm font-medium text-gray-900">3. Wie is de primaire interne contactpersoon bij calamiteiten (bijv. Hoofd BHV of HR-Manager)?</legend>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="text-sm">
                    <span className="block text-xs font-medium text-gray-700 mb-1">Naam</span>
                    <input value={contactNaam} onChange={(e) => setContactNaam(e.target.value)} placeholder="Mark de Vries" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-xs font-medium text-gray-700 mb-1">Mobiel nummer</span>
                    <input value={contactTelefoon} onChange={(e) => setContactTelefoon(e.target.value)} placeholder="06-12345678" inputMode="tel" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    <span className="block mt-1 text-[11px] text-gray-500">Cruciaal: dit nummer ontvangt de SMS-alerts bij een incident én de verificatiecode van stap 3.</span>
                  </label>
                </div>
              </fieldset>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={onClose}>Annuleren</Button>
                <Button disabled={!stap1Compleet} loading={voorbereidMutation.isPending} onClick={() => voorbereidMutation.mutate()}>
                  Naar tarief & dekking →
                </Button>
              </div>
            </>
          )}

          {/* ===== STAP 2: Tarief- & dekkingsbevestiging ===== */}
          {stap === 2 && overzicht && (
            <>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
                <strong className="text-gray-900">{overzicht.vestiging.naam}</strong>
                {overzicht.vestiging.plaats ? ` · ${overzicht.vestiging.plaats}` : ''} · {overzicht.vestiging.aantal_medewerkers} medewerkers · regio {overzicht.regio_code} · {sectorLabel}
                {voorbereiding?.screening.ploegendienst ? ' · 24/7 ploegendienst' : ' · kantooruren'} · contact {voorbereiding?.screening.contact_naam} ({voorbereiding?.screening.telefoon_masker})
              </div>

              <div className="rounded-xl border border-[#0B1F3A]/20 p-5">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  📊 Uw {overzicht.schil.naam} Crisis-Dienst Overzicht
                  <span className="text-xs font-normal text-gray-500">Schil {overzicht.schil.schil_niveau}</span>
                </h4>
                <ul className="mt-4 space-y-3 text-sm text-gray-800">
                  <li className="flex gap-3">
                    <span className="text-lg">💶</span>
                    <span>
                      <strong>Vaste Maandelijkse Retainer:</strong> {euroCrisis(overzicht.retainer_per_maand, 0)},- per maand (geïncasseerd via Stripe SEPA).
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-lg">🛡️</span>
                    <span>
                      <strong>Gegarandeerde Basis-Dekking:</strong> {overzicht.basis_vrije_uitrukken} volledige vrije uitruk{overzicht.basis_vrije_uitrukken > 1 ? 'ken' : ''} per kalenderjaar (à {euroCrisis(overzicht.uurtarief, 0)},- per uur, 100% vergoed uit het BeLagom Garantiefonds: factuur van exact € 0,-).
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-lg">🚀</span>
                    <span>
                      <strong>Inclusief No-Claim Rollover:</strong> spaar bij nul incidenten door naar maximaal {overzicht.max_no_claim_cap} vrije uitrukken in het volgende jaar.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-lg">🚨</span>
                    <span>
                      <strong>Acute Calamiteitenregie:</strong> directe toegang tot de Single-Response (1 expert) of Dual-Response (2 experts) alarmknop in het portaal. Buiten het tegoed: {euroCrisis(overzicht.uurtarief, 0)}/uur, min. {overzicht.min_uren} uur, via de SEPA-verzamelfactuur.
                    </span>
                  </li>
                </ul>
                <p className="mt-4 text-[11px] text-gray-500">
                  Verdeelsleutel van uw inleg: {Math.round(overzicht.split.BELAGOM_FEE * 100)}% platform · {Math.round(overzicht.split.POOL_SHARE * 100)}% stand-by pool · {Math.round(overzicht.split.FONDS_SHARE * 100)}% Calamiteiten-Garantiefonds.
                </p>
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={() => setStap(1)}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Screening aanpassen
                </Button>
                <Button onClick={() => setStap(3)}>Naar de digitale verzegeling →</Button>
              </div>
            </>
          )}

          {/* ===== STAP 3: Digitale SMS-verzegeling ===== */}
          {stap === 3 && voorbereiding && (
            <>
              {gelukt === 'ACTIVE' ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                  <h4 className="mt-3 font-semibold text-emerald-900">Crisisdienst actief voor {overzicht?.vestiging.naam}</h4>
                  <p className="mt-1 text-sm text-emerald-800">
                    Het Addendum Crisisdienst is digitaal verzegeld, het maandelijkse SEPA-abonnement loopt en uw No-Claim tegoed staat klaar. De grote rode alarmknop is per direct live in uw cockpit.
                  </p>
                  <Button className="mt-4" onClick={onClose}>Naar de alarmknop</Button>
                </div>
              ) : gelukt === 'PENDING_MANDATE' ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-600">
                  <Loader2 className="w-5 h-5 animate-spin" /> Addendum verzegeld — doorsturen naar de Stripe SEPA-machtiging…
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Lees de Aanvullende Gebruiksovereenkomst (Addendum Crisisdienst, versie {voorbereiding.addendum.versie}). Uw akkoord wordt verzegeld met een 6-cijferige SMS-code naar {voorbereiding.screening.telefoon_masker}.
                  </p>
                  <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-300 bg-gray-50 p-4 font-mono text-[12px] leading-relaxed text-gray-800">
                    {voorbereiding.addendum.tekst}
                  </pre>
                  <p className="text-[10px] text-gray-400 font-mono">sha256 {voorbereiding.addendum.hash}</p>

                  <label className={`flex items-start gap-3 rounded-lg border p-3 text-sm ${akkoord ? 'border-[#0B1F3A] bg-blue-50' : 'border-gray-200'}`}>
                    <input type="checkbox" checked={akkoord} disabled={akkoord && (codeMutation.isPending || Boolean(codeVerstuurd))} onChange={(e) => setAkkoord(e.target.checked)} className="mt-0.5 h-4 w-4" />
                    <span>Ik ga akkoord met de algemene voorwaarden en het Addendum Crisisdienst.</span>
                  </label>

                  {akkoord && (
                    <div className="rounded-xl border-2 border-[#0B1F3A]/30 bg-white p-5 shadow-inner">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                        <Smartphone className="w-4 h-4 text-[#0B1F3A]" />
                        {codeMutation.isPending ? 'Verificatiecode wordt verstuurd…' : codeVerstuurd ? `Voer de 6-cijferige verificatiecode in die naar ${codeVerstuurd} is verzonden:` : 'Code aanvragen…'}
                      </div>
                      {codeVerstuurd && (
                        <>
                          <div className="mt-3 flex justify-center gap-2">
                            {code.map((c, i) => (
                              <input
                                key={i}
                                ref={(el) => {
                                  codeRefs.current[i] = el;
                                }}
                                value={c}
                                inputMode="numeric"
                                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                                maxLength={6}
                                disabled={ondertekenMutation.isPending}
                                onChange={(e) => zetCijfer(i, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Backspace' && !code[i] && i > 0) codeRefs.current[i - 1]?.focus();
                                }}
                                className="h-12 w-10 rounded-lg border border-gray-300 text-center text-xl font-semibold focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A]"
                              />
                            ))}
                          </div>
                          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                            <button type="button" className="underline hover:text-gray-700" disabled={codeMutation.isPending} onClick={() => codeMutation.mutate()}>
                              Nieuwe code sturen
                            </button>
                            {ondertekenMutation.isPending && (
                              <span className="inline-flex items-center gap-1">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Verzegelen & Stripe-abonnement starten…
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between gap-2 pt-2">
                    <Button variant="outline" onClick={() => setStap(2)} disabled={ondertekenMutation.isPending}>
                      <ArrowLeft className="w-4 h-4 mr-1" /> Terug
                    </Button>
                    <Button disabled={!codeCompleet || !codeVerstuurd} loading={ondertekenMutation.isPending} onClick={() => ondertekenMutation.mutate()}>
                      🔐 Verzegel & activeer
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployerCrisisWizard;
